"use client";
import {
  AlertOctagon,
  Check,
  Copy,
  Info,
  ShieldAlert,
  ShieldCheck,
} from "lucide-react";
import {
  type ScanReport,
} from "../../../lib/types";
import {
  truncateMiddle,
} from "../../shared/format";


export function IntegrityView({
  report,
  onCopy,
}: {
  report: ScanReport;
  onCopy: (value: string, message?: string) => void;
}) {
  const statusLabels: Record<ScanReport["integrity"]["status"], string> = {
    unsigned: "Unsigned archive",
    "entry-digests-valid": "Signed entry digests match",
    tampered: "Signed contents changed",
    incomplete: "Incomplete signature set",
    unverifiable: "Signature not verifiable",
  };
  const official = report.officialFile;
  return (
    <div className="integrity-workspace">
      <section
        className={`integrity-hero status-${report.integrity.status}`}
      >
        <div>
          {report.integrity.status === "entry-digests-valid" ? (
            <ShieldCheck size={28} />
          ) : report.integrity.status === "tampered" ? (
            <AlertOctagon size={28} />
          ) : (
            <ShieldAlert size={28} />
          )}
        </div>
        <span>
          <div className="section-kicker">JAR SIGNATURE INTEGRITY</div>
          <h2>{statusLabels[report.integrity.status]}</h2>
          <p>{report.integrity.notes[0]}</p>
        </span>
        <strong>{report.integrity.coveragePercent.toFixed(1)}%</strong>
      </section>
      <div className="integrity-grid">
        <section className="panel">
          <div className="panel-heading">
            <div>
              <div className="section-kicker">DIGEST COVERAGE</div>
              <h3>Signed entries</h3>
            </div>
            <span>
              {report.integrity.verifiedEntryCount}/
              {report.integrity.signedEntryCount} verified
            </span>
          </div>
          <div className="integrity-stat-grid">
            <div>
              <span>Verified</span>
              <strong>{report.integrity.verifiedEntryCount}</strong>
            </div>
            <div>
              <span>Invalid</span>
              <strong>{report.integrity.invalidEntries.length}</strong>
            </div>
            <div>
              <span>Missing</span>
              <strong>{report.integrity.missingEntries.length}</strong>
            </div>
            <div>
              <span>Unsigned</span>
              <strong>{report.integrity.unsignedEntries.length}</strong>
            </div>
          </div>
          {report.integrity.invalidEntries.length > 0 && (
            <div className="invalid-digest-list">
              {report.integrity.invalidEntries.map((entry) => (
                <div key={entry.path}>
                  <AlertOctagon size={15} />
                  <span>
                    <strong>{entry.path}</strong>
                    <code>{entry.algorithm}</code>
                    <small>
                      expected {truncateMiddle(entry.expected)} · actual{" "}
                      {truncateMiddle(entry.actual)}
                    </small>
                  </span>
                </div>
              ))}
            </div>
          )}
          <div className="signature-files">
            {report.integrity.signatureFiles.map((path) => (
              <code key={path}>{path}</code>
            ))}
            {!report.integrity.signatureFiles.length && (
              <span>No signature metadata</span>
            )}
          </div>
        </section>
        <section className="panel official-release-panel">
          <div className="panel-heading">
            <div>
              <div className="section-kicker">HASH-ONLY PROVENANCE</div>
              <h3>Modrinth release identity</h3>
            </div>
            <span className={`official-status status-${official.status}`}>
              {official.status}
            </span>
          </div>
          <div className="official-release-body">
            {official.status === "matched" ? (
              <>
                <span className="official-check">
                  <Check size={20} />
                </span>
                <div>
                  <h4>
                    {official.projectTitle ??
                      official.projectSlug ??
                      official.projectId}
                  </h4>
                  <p>
                    {official.versionName} · {official.versionNumber}
                  </p>
                  <code>{official.officialFileName}</code>
                  <div className="tag-row">
                    {official.loaders?.map((loader) => (
                      <span key={loader}>{loader}</span>
                    ))}
                    {official.gameVersions?.slice(0, 6).map((version) => (
                      <span key={version}>{version}</span>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="official-message">
                <Info size={18} />
                <p>{official.message}</p>
              </div>
            )}
          </div>
          <div className="payload-hash">
            <span>CHECKED SHA-512</span>
            <button
              type="button"
              onClick={() =>
                onCopy(official.checkedHash, "SHA-512 copied")
              }
            >
              <code>{truncateMiddle(official.checkedHash, 24, 18)}</code>
              <Copy size={13} />
            </button>
          </div>
        </section>
      </div>
      <section className="panel fingerprint-panel">
        <div className="panel-heading">
          <div>
            <div className="section-kicker">REPACK-RESISTANT IDENTITY</div>
            <h3>Normalized behavior fingerprints</h3>
          </div>
          <span>
            {report.archiveFingerprint.methodCount.toLocaleString()} methods
          </span>
        </div>
        <div className="fingerprint-grid">
          {[
            [
              "Normalized archive",
              report.archiveFingerprint.normalizedSha256,
            ],
            ["Semantic archive", report.archiveFingerprint.semanticSha256],
            ["Behavior SimHash", report.archiveFingerprint.simhash64],
          ].map(([label, value]) => (
            <button
              type="button"
              key={label}
              onClick={() => onCopy(value, `${label} copied`)}
            >
              <span>{label}</span>
              <code>{value}</code>
              <Copy size={13} />
            </button>
          ))}
        </div>
        <div className="feature-cloud">
          {Object.entries(report.archiveFingerprint.featureCounts)
            .sort((left, right) => right[1] - left[1])
            .map(([feature, count]) => (
              <span key={feature}>
                {feature}
                <small>{count}</small>
              </span>
            ))}
        </div>
      </section>
    </div>
  );
}
